/**
 * Application JavaScript principale pour le thème 3F
 * Point d'entrée pour tous les modules JS
 */

// Import du CSS principal
import "../scss/main.scss";

// Import des modules JavaScript
import { AnimationController } from "./modules/AnimationController.js";
import { ScrollManager } from "./modules/ScrollManager.js";
import { ComponentLoader } from "./modules/ComponentLoader.js";

// Import des composants
import { HeaderSearch } from "./components/header-search.js";
import { Header } from "./components/header.js";
import MobileNav from "./components/mobile-nav.js";
import DesktopMenu from "./components/desktop-menu.js";
import BackToTop from "./components/back-to-top.js";
import "./components/breadcrumb-adaptive-color.js";
import SearchFilters from "./modules/search-filters.js";
import ThematiqueFilter from "./modules/thematique-filter.js";

/**
 * Classe principale de l'application
 */
class ThreeFApp {
  constructor() {
    this.animationController = new AnimationController();
    this.scrollManager = new ScrollManager();
    this.componentLoader = new ComponentLoader();
    this.headerSearch = new HeaderSearch();
    this.header = new Header();
    this.mobileNav = new MobileNav();
    this.desktopMenu = new DesktopMenu();
    this.backToTop = new BackToTop();
    this.searchFilters = new SearchFilters();
    this.thematiqueFilter = new ThematiqueFilter();

    this.init();
  }

  /**
   * Initialisation de l'application
   */
  async init() {
    // Activer les transitions IMMÉDIATEMENT - pas de délai pour Lighthouse
    document.body.classList.add("loaded");

    // Attendre que Drupal soit prêt
    if (typeof Drupal !== "undefined" && Drupal.behaviors) {
      this.initDrupalIntegration();
    }

    // Initialiser les modules
    await this.initModules();

    // Charger les composants à la demande
    this.setupLazyLoading();
  }

  /**
   * Intégration avec Drupal behaviors
   */
  initDrupalIntegration() {
    const self = this;

    Drupal.behaviors.threeFTheme = {
      attach: function (context, settings) {
        // Re-initialiser les modules pour le nouveau contenu
        self.animationController.attach(context);
        self.scrollManager.attach(context);
      },
    };
  }

  /**
   * Initialisation des modules
   */
  async initModules() {
    try {
      await this.animationController.init();
      await this.scrollManager.init();
      await this.componentLoader.init();
    } catch (error) {
      console.error("Error initializing modules:", error);
    }
  }

  /**
   * Configuration du chargement paresseux des composants CSS
   */
  setupLazyLoading() {
    // Observer pour détecter les nouveaux composants
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.componentLoader.loadComponentStyles(node);
            }
          }, 100); // 100ms après le chargement complet
        }
      }, 100); // 100ms après le chargement complet
    }, 100); // 100ms après le chargement complet

    // Observer le DOM pour les changements
    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true,
      },
      100,
    ); // 100ms après le chargement complet

    // Charger les composants existants
    this.componentLoader.loadComponentStyles(document.body);
  }
}

// Initialiser l'application quand le DOM est prêt
if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => {
      new ThreeFApp();
    },
    100,
  ); // 100ms après le chargement complet
} else {
  new ThreeFApp();
}

// Export pour usage global si nécessaire
window.ThreeFApp = ThreeFApp;
